import React from 'react'
import { Grid, Typography } from '@the-platform-group/component-library'
import { getFullName } from '@the-platform-group/formatters/people'
import { usePeopleMergeContext } from 'pages/MergeProfiles/contexts/PeopleMergeContext'
import {
  getJobTitleAndOrgName,
  getJobFieldValue,
  getJobOverrideValue,
} from 'pages/MergeProfiles/utils'
import { JOB_DETAILS_DRAWER_FIELDS } from 'pages/MergeProfiles/constants'
import JobDetailsTable from '../JobDetailsTable'
import BaseJobDrawer from '../MultipleJobsSection/BaseJobDrawer'

/**
 * JobDetailsDrawer - Read-only job details view in the final review step
 * This drawer displays the final mapped job details before submitting the merge.
 * It shows:
 * - FactSet job information
 * - Mapped Platform jobs
 * - Field-by-field comparison showing which values were overridden
 * @param {Object} props
 * @param {boolean} props.open - Controls drawer visibility
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {string} props.selectedTargetJobId - The FactSet job ID to display details for
 *
 * @returns {React.Component|null} The job details drawer or null if no job is selected
 */
const JobDetailsDrawer = ({ open, onClose, selectedTargetJobId }) => {
  const { mergeData, jobMappingData } = usePeopleMergeContext()
  const { targetJobs, targetPerson, sourceJobs } = mergeData
  const { getMappedSourceJobsForTarget } = jobMappingData

  const selectedTargetJob = !selectedTargetJobId
    ? null
    : targetJobs.find(job => job.id === selectedTargetJobId)

  const mappedSourceJobs = getMappedSourceJobsForTarget(selectedTargetJobId, sourceJobs)

  const getOverrideValue = fieldKey => {
    return getJobOverrideValue(mappedSourceJobs, fieldKey)
  }

  if (!selectedTargetJob) {
    return null
  }

  return (
    <BaseJobDrawer open={open} onClose={onClose} title="Job Details">
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Typography variant="body1" fontWeight="bold">
            {getFullName(targetPerson)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            FactSet Job: {getJobTitleAndOrgName(selectedTargetJob)}
          </Typography>
        </Grid>

        {mappedSourceJobs.length > 0 && (
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Mapped Platform Jobs:
            </Typography>
            {mappedSourceJobs.map(job => (
              <Typography key={job.id} variant="body2" sx={{ ml: 2 }}>
                {getJobTitleAndOrgName(job)}
              </Typography>
            ))}
          </Grid>
        )}

        <Grid item>
          <JobDetailsTable
            fields={JOB_DETAILS_DRAWER_FIELDS}
            selectedTargetJob={selectedTargetJob}
            getFieldValue={getJobFieldValue}
            getOverrideValue={getOverrideValue}
          />
        </Grid>
      </Grid>
    </BaseJobDrawer>
  )
}

export default JobDetailsDrawer
